import { getSheetData } from './spreadsheet';

export function parseTemplateWorkbook(workbook) {
    const errors = [];
    return {
        accounts: parseAccountsSheet(workbook),
        associatedAccounts: parseAssociatedAccountsSheet(workbook),
        formulas: parseFormulasSheet(workbook),
        errors,
    };
}

const financialGroupTypeAndName = {
    BSA: [2, 1],
    BSL: [3, 1],
    BSE: [4, 1],
    PL: [1, 2],
    CF: [5, 3],
    DRVE: [6, 4],
};

const sectionAliases = {
    PL: 'PL',
    'P&L': 'PL',
    PROFITLOSS: 'PL',
    'PROFIT LOSS': 'PL',
    'PROFIT & LOSS': 'PL',

    BSA: 'BSA',
    ASSETS: 'BSA',
    'BALANCE SHEET ASSETS': 'BSA',

    BSL: 'BSL',
    LIABILITIES: 'BSL',
    'BALANCE SHEET LIABILITIES': 'BSL',

    BSE: 'BSE',
    EQUITY: 'BSE',
    EQUITIES: 'BSE',
    'BALANCE SHEET EQUITY': 'BSE',

    CF: 'CF',
    CASHFLOW: 'CF',
    'CASH FLOW': 'CF',

    DRVE: 'DRVE',
    DRIVERS: 'DRVE',
};

function getSectionGroups(row, headerMap) {
    const sectionValue = row[headerMap.get('section')];

    if (sectionValue !== undefined && sectionValue !== null && sectionValue !== '') {
        const raw = String(sectionValue).trim().toUpperCase();
        const normalized = sectionAliases[raw];

        if (normalized && financialGroupTypeAndName[normalized]) {
            const [financial_group_type, group_id] = financialGroupTypeAndName[normalized];

            return {
                financial_group_type,
                group_id,
                section: normalized,
            };
        }
    }

    return {
        group_id: Number(row[headerMap.get('group_id')]) || null,
        financial_group_type: Number(row[headerMap.get('financial_group_type')]) || null,
        section: null,
    };
}

function normalizeHeader(value) {
    return String(value ?? '')
        .trim()
        .toLowerCase();
}

function buildHeaderMap(headerRow) {
    const map = new Map();

    headerRow.forEach((cell, index) => {
        map.set(normalizeHeader(cell), index);
    });

    return map;
}

function getNamedSheetData(workbook, sheetName) {
    const sheetIndex = workbook.SheetNames.findIndex(
        (name) => name.trim().toLowerCase() === sheetName.trim().toLowerCase(),
    );

    if (sheetIndex < 0) {
        return [];
    }

    return getSheetData(workbook, false, sheetIndex);
}

function parseBoolean(value) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'boolean') return value;

    const normalized = String(value).trim().toLowerCase();

    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;

    return null;
}


function parseAccountsSheet(workbook) {
    const rows = getNamedSheetData(workbook, 'Accounts');


    if (!rows?.length) {
        return [];
    }

    const headerMap = buildHeaderMap(rows[0]);

    return rows
        .slice(1)
        .filter(row => row.some(v => v !== undefined && v !== ''))
       .map((row, index) => {
        
        const groups = getSectionGroups(row, headerMap);
        
        return {
            _row: index + 2,
            
            account_number:
                row[headerMap.get('account_number')]?.toString().trim(),

            parent_account_number:
                row[headerMap.get('parent_account_number')]?.toString().trim() ?? null,

            name:
                row[headerMap.get('name')] ?? null,

            group_id:
                groups.group_id,

            financial_group_type:
                groups.financial_group_type,

            sum_type:
                row[headerMap.get('sum_type')] ?? null,

            weight:
                row[headerMap.get('weight')]?.toString() ?? null,

            numeric:
                parseBoolean(row[headerMap.get('numeric')]),

            ratio:
                parseBoolean(row[headerMap.get('ratio')]),

            unit:
                parseBoolean(row[headerMap.get('unit')]),

            is_change_in:
                parseBoolean(row[headerMap.get('is_change_in')]),

            carry:
                parseBoolean(row[headerMap.get('carry')]),

            convergence:
                parseBoolean(row[headerMap.get('convergence')]),

            exclude:
                parseBoolean(row[headerMap.get('exclude')]),

            exclude_bc:
                parseBoolean(row[headerMap.get('exclude_bc')]),

            roll_up:
                row[headerMap.get('roll_up')] ?? null,

            // Raw text; the server normalizes aliases like "derived" and rejects unknown values.
            bc_combine:
                row[headerMap.get('bc_combine')]?.toString().trim() || null,

            bc_weight:
                row[headerMap.get('bc_weight')]?.toString().trim() || null,

            index:
                Number(row[headerMap.get('index')]) || null,
        };
    });
}

function parseAssociatedAccountsSheet(workbook) {
    const rows = getNamedSheetData(workbook, 'AssociatedAccounts');
   
    if (!rows?.length) {
        return [];
    }

    const headerMap = buildHeaderMap(rows[0]);

    return rows
        .slice(1)
        .filter(row => row.some(v => v !== undefined && v !== ''))
        .map((row, index) => {
            const groups = getSectionGroups(row, headerMap);

            return {
                _row: index + 2,

                associated_account_number:
                    row[headerMap.get('associated_account_number')]?.toString().trim(),

                account_number:
                    row[headerMap.get('account_number')]?.toString().trim(),

                parent_account_number:
                    row[headerMap.get('parent_account_number')]?.toString().trim() ?? null,

                name:
                    row[headerMap.get('name')] ?? null,

                group_id:
                    groups.group_id,

                financial_group_type:
                    groups.financial_group_type,

                numeric:
                    parseBoolean(row[headerMap.get('numeric')]),

                ratio:
                    parseBoolean(row[headerMap.get('ratio')]),

                unit:
                    parseBoolean(row[headerMap.get('unit')]),

                cash_effect:
                    parseBoolean(row[headerMap.get('cash_effect')]),

                carry:
                    parseBoolean(row[headerMap.get('carry')]),

                convergence:
                    parseBoolean(row[headerMap.get('convergence')]),

                exclude:
                    parseBoolean(row[headerMap.get('exclude')]),

                exclude_bc:
                    parseBoolean(row[headerMap.get('exclude_bc')]),

                roll_up:
                    row[headerMap.get('roll_up')] ?? null,

                index:
                    Number(row[headerMap.get('index')]) || null,
            };
        });
}


function isFormulaMetaColumn(header) {
    return [
        'account_number',
        'associated_account_number',
        'formula',
        'forecast',
        'budget',
        'period',
        'period_year',
        'period_month',
        'period_quarter',
    ].includes(normalizeHeader(header));
}


function parseFormulasSheet(workbook) {
    const rows = getNamedSheetData(workbook, 'Formulas');

    if (!rows?.length) {
        return [];
    }

    const headers = rows[0];

    
    const headerMap = buildHeaderMap(headers);

    const hasSingleFormulaColumn = headerMap.has('formula');
    const hasPeriodColumn = headerMap.has('period');

    // Narrow format
    if (hasSingleFormulaColumn && hasPeriodColumn) {
        return rows
            .slice(1)
            .filter(row => row.some(v => v !== undefined && v !== ''))
            .map((row, index) => ({
                _row: index + 2,

                account_number:
                    row[headerMap.get('account_number')]?.toString().trim() ?? null,

                associated_account_number:
                    row[headerMap.get('associated_account_number')]?.toString().trim() ?? null,

                formula:
                    row[headerMap.get('formula')] ?? '',

                forecast:
                    parseBoolean(row[headerMap.get('forecast')]),

                budget:
                    parseBoolean(row[headerMap.get('budget')]),

                period:
                    row[headerMap.get('period')]?.toString() ?? '',

                period_year:
                    row[headerMap.get('period_year')]?.toString()
                    ?? row[headerMap.get('period')]?.toString()
                    ?? '',

                period_month:
                    row[headerMap.get('period_month')]?.toString() ?? '',

                period_quarter:
                    row[headerMap.get('period_quarter')]?.toString() ?? '',
            }));
    }

    // Wide format: every non-meta header is treated as a period
    const periodColumns = headers
        .map((header, index) => ({ 
            index,
            period: String(header ?? '').trim(), 
        }))

    .filter(col => col.period && !isFormulaMetaColumn(col.period));
    
    return rows
    .slice(1)
    .filter(row => row.some(v => v !== undefined && v !== null && v !== ''))
    .flatMap((row, rowIndex) => {
        const accountNumber =
            row[headerMap.get('account_number')]?.toString().trim() ?? null;

            const associatedAccountNumber =
                row[headerMap.get('associated_account_number')]?.toString().trim() ?? null;

            return periodColumns
                 .filter(col => {
                    const value = row[col.index];
                    return value !== undefined && value !== null && String(value).trim() !== '';
                })
                .map(col => ({
                    _row: rowIndex + 2,
                    _col: col.index + 1,

                    account_number: accountNumber,

                    associated_account_number: associatedAccountNumber,

                    formula: String(row[col.index]).trim(),

                    forecast:
                        parseBoolean(row[headerMap.get('forecast')]),

                    budget:
                        parseBoolean(row[headerMap.get('budget')]),

                    period: col.period,

                    period_year: col.period,

                    period_month: '',

                    period_quarter: '',
                    
                }));
                
        });
        
}
